/**
 * User Switcher Component (Dev Only)
 *
 * Dropdown to quickly switch between demo user accounts for testing.
 * Uses fixed positioning so the dropdown is never clipped by overflow.
 * Navigates to the user's dashboard after switching.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiChevronDown, FiChevronUp, FiUser } from 'react-icons/fi'
import { useAuthStore, useDemoModeStore } from '../../store'
import { cn } from '../../lib/utils'
import type { UserRole } from '../../types'

interface DemoUser {
  email: string
  password: string
  displayName: string
  role: UserRole
}

const demoUsers: DemoUser[] = [
  { email: 'demo@docurious.com', password: 'DcDemo_2026!', displayName: 'Demo User', role: 'user' },
  { email: 'platform@docurious.com', password: 'DcPlatfm_2026!', displayName: 'Platform Admin', role: 'platform_admin' },
  { email: 'admin@lincoln-elementary.edu', password: 'DcAdmin_2026!', displayName: 'Sarah Johnson', role: 'school_admin' },
  { email: 'head-admin@lincoln-elementary.edu', password: 'DcHeadAd_2026!', displayName: 'District Admin', role: 'head_school_admin' },
  { email: 'teacher@lincoln-elementary.edu', password: 'DcTeach_2026!', displayName: 'Mr. Thompson', role: 'teacher' },
  { email: 'student@lincoln-elementary.edu', password: 'DcStudent_2026!', displayName: 'Alex Student', role: 'student' },
  { email: 'parent@example.com', password: 'DcParent_2026!', displayName: 'Alex Parent', role: 'parent' },
  { email: 'vendor@craftbox.com', password: 'DcVendor_2026!', displayName: 'CraftBox', role: 'vendor' },
  { email: 'staff@docurious.com', password: 'DcStaff_2026!', displayName: 'Content Manager', role: 'staff' },
]

interface UserSwitcherProps {
  /** When true (default), dropdown opens upward. Set false for top-of-page contexts. */
  dropUp?: boolean
}

export function UserSwitcher({ dropUp = true }: UserSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const dropRef = useRef<HTMLDivElement>(null)
  const user = useAuthStore((s) => s.user)
  const login = useAuthStore((s) => s.login)
  const navigate = useNavigate()
  const isDemoActive = useDemoModeStore((s) => s.isActive)

  // Hide during demo mode — use the demo bar to switch personas instead
  if (isDemoActive) return null

  // Calculate position when opening
  const updatePosition = useCallback(() => {
    if (!btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    setPos({ top: rect.top, left: rect.left, width: rect.width })
  }, [])

  useEffect(() => {
    if (!isOpen) return
    updatePosition()

    // Close on click outside
    const handleClickOutside = (e: MouseEvent) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target as Node) &&
        dropRef.current && !dropRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, updatePosition])

  const handleSwitch = async (demoUser: DemoUser) => {
    setIsLoading(true)
    try {
      const success = await login({ email: demoUser.email, password: demoUser.password })
      setIsOpen(false)
      if (success) {
        navigate('/')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Compute dropdown style: fixed position, clamped to viewport
  const dropdownStyle = (): React.CSSProperties | undefined => {
    if (!pos) return { position: 'fixed', opacity: 0, pointerEvents: 'none' }
    const maxH = 400
    if (dropUp) {
      const bottomY = pos.top - 4
      const availableUp = bottomY
      const height = Math.min(maxH, availableUp)
      return {
        position: 'fixed',
        bottom: `${window.innerHeight - bottomY}px`,
        left: `${pos.left}px`,
        width: `${Math.max(pos.width, 200)}px`,
        maxHeight: `${height}px`,
        zIndex: 9999,
      }
    } else {
      const topY = pos.top + (btnRef.current?.offsetHeight ?? 36) + 4
      const availableDown = window.innerHeight - topY
      const height = Math.min(maxH, availableDown)
      return {
        position: 'fixed',
        top: `${topY}px`,
        left: `${pos.left}px`,
        width: `${Math.max(pos.width, 200)}px`,
        maxHeight: `${height}px`,
        zIndex: 9999,
      }
    }
  }

  return (
    <>
      <button
        ref={btnRef}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 rounded-[var(--radius-button)] text-sm font-medium text-sidebar-text hover:bg-sidebar-active transition-colors cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <FiUser size={14} />
          <span>Switch User</span>
        </div>
        {isOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
      </button>

      {isOpen && (
        <div
          ref={dropRef}
          className="bg-background border border-border rounded-[var(--radius-card)] shadow-lg overflow-y-auto"
          style={dropdownStyle()}
        >
          <div className="py-1">
            {demoUsers.map((demoUser) => {
              const isCurrentUser = user?.email === demoUser.email
              return (
                <div
                  key={demoUser.email}
                  className={cn(
                    'flex items-center justify-between px-3 py-2 text-sm cursor-pointer transition-colors',
                    isCurrentUser
                      ? 'bg-primary/10 text-primary'
                      : 'text-foreground hover:bg-background-secondary',
                    (isLoading || isCurrentUser) && 'opacity-60 cursor-not-allowed'
                  )}
                  onClick={() => !isCurrentUser && !isLoading && handleSwitch(demoUser)}
                  aria-disabled={isLoading || isCurrentUser}
                >
                  <span className="font-medium truncate">
                    {demoUser.displayName}
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary ml-2 shrink-0">
                    {demoUser.role.replace('_', ' ')}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
